import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Product } from "../../utils/api/product/product.type";
import { getEffectiveCatalogUnitPrice } from "../../utils/catalogProductPrice";
import { toShopTvaSettings } from "../../utils/tvaDisplayPrice";


type Cart = {
  cartItems: CartItemType[];
  totalCount: number;
  tax: number;
  delivery: number;
  subAmount: number;
  totalAmount: number;
};

export type CartItemType = {
  produit: Product ;
  prix: number ;
  quantite: number;
  stock: number;
  variations: any;
  vendeur: number
};
const initialState: Cart = {
  cartItems: [],
  totalCount: 0,
  delivery: 0,
  tax: 0,
  subAmount: 0,
  totalAmount: 0,
};

function refreshCartItemUnitPrice(item: CartItemType): void {
  const variation = Array.isArray(item?.variations) ? item.variations[0] : null;
  const tvaSettings = toShopTvaSettings(
    (item?.produit as any)?.vendeur ?? null,
  );
  const pricing = getEffectiveCatalogUnitPrice(
    item?.produit,
    variation,
    item?.quantite || 0,
    { sizeSelected: true },
    tvaSettings,
  );
  item.prix = pricing.displayPrice;
}

function syncCartTotals(state: Cart) {
  state.totalCount = state.cartItems.reduce(
    (total, item) => total + (item?.quantite ?? 0),
    0,
  );
  state.totalAmount = state.cartItems.reduce(
    (total, item) => total + (item?.quantite ?? 0) * (item?.prix ?? 0),
    0,
  );
}

function findCartItemIndex(
  items: CartItemType[],
  payload: CartItemType,
): number {
  const payloadProductId = payload?.produit?.id;
  const payloadVariationId = payload?.variations?.[0]?.id ?? null;

  return items.findIndex(
    (item) =>
      item?.produit?.id == payloadProductId &&
      (item?.variations?.[0]?.id ?? null) == payloadVariationId,
  );
}

const useCartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addCartProduct: (state, action: PayloadAction<CartItemType>) => {
      const payloadProductId = action.payload?.produit?.id;
      const payloadVariationId = action.payload?.variations?.[0]?.id ?? null;

      const cartIndex = state.cartItems.findIndex(
        (item) =>
          item?.produit?.id == payloadProductId &&
          (item?.variations?.[0]?.id ?? null) == payloadVariationId,
      );

      if (cartIndex !== -1) {
        state.cartItems[cartIndex].quantite += action?.payload?.quantite;
        refreshCartItemUnitPrice(state.cartItems[cartIndex]);
      } else {
        const next = { ...action.payload };
        refreshCartItemUnitPrice(next);
        state.cartItems.push(next);
      }
      syncCartTotals(state);
    },
    getCartProducts: (state) => {
      return {
        ...state,
      };
    },
    getCartCount: (state) => {
      let cartCount = state.cartItems.reduce((total, item) => {
        return item?.quantite + total;
      }, 0);
      state.totalCount = cartCount;
    },
    // getSubTotal: (state) => {
    //   state.subAmount = state.cartItems.reduce((acc, item) => {
    //     return (
    //       acc +
    //       (item?.product?.promote?.price || item?.product?.prix) * item?.quantite
    //     );
    //   }, 0);
    // },
    removeCartItem: (state, action: PayloadAction<CartItemType>) => {
      const index = findCartItemIndex(state.cartItems, action.payload);

      if (index !== -1) {
        state.cartItems.splice(index, 1);
        syncCartTotals(state);
      }
    },
    increment: (state, action: PayloadAction<CartItemType>) => {
      const payloadProductId = action.payload?.produit?.id;
      const payloadVariationId = action.payload?.variations?.[0]?.id ?? null;

      const index = state.cartItems.findIndex(
        (item) =>
          item?.produit?.id == payloadProductId &&
          (item?.variations?.[0]?.id ?? null) == payloadVariationId,
      );
      if (
        index !== -1 &&
        state.cartItems[index].stock > state.cartItems[index].quantite
      ) {
        state.cartItems[index].quantite += 1;
        refreshCartItemUnitPrice(state.cartItems[index]);
        syncCartTotals(state);
      }
    },
    decrement: (state, action: PayloadAction<CartItemType>) => {
      const payloadProductId = action.payload?.produit?.id;
      const payloadVariationId = action.payload?.variations?.[0]?.id ?? null;

      const index = state.cartItems.findIndex(
        (item) =>
          item?.produit?.id == payloadProductId &&
          (item?.variations?.[0]?.id ?? null) == payloadVariationId,
      );
      if (index === -1) return;

      if (state.cartItems[index].quantite <= 1) {
        state.cartItems.splice(index, 1);
      } else {
        state.cartItems[index].quantite -= 1;
        refreshCartItemUnitPrice(state.cartItems[index]);
      }
      syncCartTotals(state);
    },
    calculateTax: (state) => {
      // GST value: 18% => action.payload
      let totalTax = (18 / 100) * state.subAmount;
      state.tax = totalTax;
    },
    getDeliveryAmount: (state,action:PayloadAction<number>) => {
     
      state.delivery = action.payload;
    },
    getTotalAmount: (state) => {
      let somme = 0
      state?.cartItems.forEach(element => {
        somme = somme + (element?.quantite * element?.prix)
      });
      state.totalAmount = somme ;
    },
    setCartItems: (state, action: PayloadAction<CartItemType[]>) => {
      state.cartItems = action.payload.map((item) => {
        const next = { ...item };
        refreshCartItemUnitPrice(next);
        return next;
      });
      syncCartTotals(state);
    },
    removeCart: (state) => {
      state = initialState;
      return state;
    },
  },
});

export const {
  addCartProduct,
  getCartProducts,
  removeCartItem,
  getCartCount,
  //getSubTotal,
  increment,
  decrement,
  calculateTax,
  getTotalAmount,
  removeCart,
  getDeliveryAmount,
  setCartItems,
} = useCartSlice.actions;

export default useCartSlice.reducer;
